import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { t } from 'services/translation';
import TextSelect from 'ui_system/select/TextSelect';
import Dimmer from 'views/shared/ui/dimmer';
import { GuestNameInput } from './GuestNameInput';
import { formatTicketTypes, calculateRemainingTickets } from './Helpers';
import {
  GuestInformationContainer,
  FormFieldsContainer,
  SectionHeader,
  StepNumber,
  GuestSection,
  GuestLabel,
  TicketSelectContainer,
  TicketSelectWrapper,
  DisabledInput,
} from './styles';

interface GuestInformationProps {
  bookerGuestName: string;
  units: any[];
  unitsLoaded: boolean;
  onChange: (
    position: number,
    value: string,
    nameField: string,
    unitTypeId: number
  ) => void;
  stepNo: number;
  lastSection?: boolean;
  brandColor?: string;
  additionalGuests?: Array<{
    position: number;
    first_name?: string;
    last_name?: string;
    unit_type_id?: number;
  }>;
}

export const GuestInformation = ({
  bookerGuestName,
  units,
  unitsLoaded,
  onChange,
  stepNo,
  lastSection = false,
  brandColor,
  additionalGuests = [],
}: GuestInformationProps) => {
  const intl = useIntl();

  const unitsSelectOptions = formatTicketTypes(units);
  const initialSelectedTicket = unitsSelectOptions[0]?.value || null;
  const [selectedTicket, setSelectedTicket] = useState<string | null>(
    initialSelectedTicket
  );

  // If no valid ticket options, return early or handle gracefully
  if (unitsSelectOptions.length === 0) {
    return <Dimmer fixed transparent loader active />;
  }

  const translatedUnitsSelectOptions = unitsSelectOptions.map(
    (option: any) => ({
      ...option,
      label: t(option.label.toUpperCase()),
    })
  );

  // Early return if units are not loaded or units is undefined/null
  if (!unitsLoaded || !units) {
    return <Dimmer fixed transparent loader active />;
  }

  function onSelected(value: string) {
    setSelectedTicket(value);
    // Handle booker unit ID selection - this should be handled by the parent component
    // We'll call onChange with the special 'bookerUnitId' position (use 0 as a special position)
    const unitId = units.find(
      (u) => u.noun_singular.toLowerCase() === value.toLowerCase()
    )?.id;
    if (unitId) {
      onChange(0, unitId.toString(), 'bookerUnitId', unitId);
    }
  }

  let displayBookerGuestName;
  if (bookerGuestName !== '' && bookerGuestName !== ' ') {
    displayBookerGuestName = bookerGuestName;
  }

  const remainingTickets = calculateRemainingTickets(units, selectedTicket);
  const hasMultipleUnits = unitsSelectOptions && unitsSelectOptions.length > 1;

  return (
    <GuestInformationContainer
      data-testid="guest-information-section"
      lastSection={lastSection}
    >
      <SectionHeader>
        <StepNumber>{stepNo}</StepNumber>
        <FormattedMessage
          id="GUEST_INFORMATION"
          defaultMessage="Guest Information"
          description="Section heading for collecting information about guests attending the experience"
        />
      </SectionHeader>

      <FormFieldsContainer>
        {/* Primary Guest */}
        <GuestSection>
          <GuestLabel data-testid="booker-guest-label">
            <FormattedMessage
              defaultMessage="Guest {guestNumber}"
              description="Label for identifying each guest by number (e.g., Guest 1, Guest 2)"
              values={{ guestNumber: 1 }}
            />
          </GuestLabel>

          {hasMultipleUnits && selectedTicket && (
            <TicketSelectContainer>
              <TicketSelectWrapper>
                <TextSelect
                  selectedLabel={t(selectedTicket.toUpperCase())}
                  selectedValue={t(selectedTicket.toUpperCase())}
                  onSelectItem={onSelected}
                  items={translatedUnitsSelectOptions}
                  brandColor={brandColor}
                  unitsSelectOptions={translatedUnitsSelectOptions}
                  customDropdown
                  dataTestId="ticket-label-select"
                  flexibleHeight={false}
                  isOpenable
                  type="text"
                  onOpenChange={() => {}}
                />
              </TicketSelectWrapper>
            </TicketSelectContainer>
          )}

          <DisabledInput
            data-testId="booker-guest-name-input"
            name="Guest 1"
            placeholder={intl.formatMessage({
              defaultMessage: 'You',
              description:
                'Placeholder text indicating the primary guest (the person making the booking)',
            })}
            maxLength={150}
            disabled
            value={displayBookerGuestName || ''}
          />
        </GuestSection>

        {/* Additional Guests */}
        {remainingTickets.length > 0 && (
          <div>
            {remainingTickets.map(
              (
                {
                  unitTypeId,
                  unitTypeName,
                }: {
                  unitTypeId: number;
                  unitTypeName: string;
                },
                index: number
              ) => {
                // Find the matching guest by position (index + 2) and unit_type_id
                const position = index + 2;

                const matchingGuest = additionalGuests.find(
                  (guest) =>
                    guest.position === position &&
                    guest.unit_type_id === unitTypeId
                );

                const firstNameValue = matchingGuest?.first_name || '';
                const lastNameValue = matchingGuest?.last_name || '';

                return (
                  <GuestNameInput
                    data-testId={`additional-guest-name-input-${index}`}
                    key={`${position}-${unitTypeId}`}
                    index={index}
                    selectedTicket={selectedTicket || ''}
                    onChange={(
                      _idx: number,
                      value: string,
                      fieldName: string,
                      unitId: number
                    ) => {
                      // Call the parent's _additionalGuestInputChanged method
                      // which expects (position, value, nameField, unitTypeId)
                      // Position starts at 2 because position 1 is the booker guest
                      onChange(index + 2, value, fieldName, unitId);
                    }}
                    unitTypeId={unitTypeId}
                    unitTypeName={t(unitTypeName.toUpperCase())}
                    hasMultipleUnits={hasMultipleUnits}
                    dataTestId={`additional-guest-name-input-${index}`}
                    firstName={firstNameValue}
                    lastName={lastNameValue}
                  />
                );
              }
            )}
          </div>
        )}
      </FormFieldsContainer>
    </GuestInformationContainer>
  );
};
